import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle, Circle, ArrowRight, Star } from "@phosphor-icons/react";
import api from "../lib/api";

/**
 * ProfileCompletenessCard — owner dashboard widget.
 *
 * Shows the owner exactly how close their public `/t/:slug` profile is
 * to the "Complete" threshold (5-of-7 signals) that bumps them above
 * other free trucks in the `/profiles` directory.
 *
 * Each signal is a checklist row. Tapping any incomplete row routes to
 * the profile editor where they can fix it. Hides itself entirely while
 * loading so the dashboard doesn't pop a placeholder card.
 *
 * Backend: GET /api/owner/profile-completeness
 *   → {score, total, threshold, is_complete, signals[], truck_slug, ...}
 */
export default function ProfileCompletenessCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    api.get("/owner/profile-completeness")
      .then(({ data }) => mounted && setData(data))
      .catch(() => {})
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, []);

  if (loading || !data || !data.has_profile) return null;

  const { score, total, threshold, is_complete, signals } = data;
  const pct = Math.round((score / total) * 100);
  const remaining = Math.max(0, threshold - score);

  return (
    <section
      className="rounded-2xl border border-border bg-card p-5 sm:p-6"
      data-testid="profile-completeness-card"
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <h2 className="font-display font-bold text-lg sm:text-xl">
            Profile completeness
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            {is_complete ? (
              <>
                <CheckCircle weight="fill" className="inline h-3.5 w-3.5 text-emerald-500 -mt-0.5" />{" "}
                Complete profiles rank higher in the public directory.
              </>
            ) : remaining === 1 ? (
              <>One more signal and you'll be marked <strong>Complete</strong> in the directory.</>
            ) : (
              <>Add {remaining} more {remaining === 1 ? "thing" : "things"} to be marked <strong>Complete</strong> in the directory.</>
            )}
          </p>
        </div>
        <div className="flex-none text-right">
          <div className="font-display font-bold text-2xl tabular-nums" data-testid="profile-completeness-score">
            {score}<span className="text-muted-foreground text-base">/{total}</span>
          </div>
          {is_complete && (
            <div className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-emerald-600 dark:text-emerald-400">
              <Star weight="fill" size={10} /> Complete
            </div>
          )}
        </div>
      </div>

      {/* Progress bar */}
      <div className="h-2 bg-muted rounded-full overflow-hidden" aria-hidden>
        <div
          className={`h-full rounded-full transition-all duration-500 ${
            is_complete ? "bg-emerald-500" : "bg-primary"
          }`}
          style={{ width: `${pct}%` }}
          data-testid="profile-completeness-bar"
        />
      </div>

      {/* Checklist */}
      <ul className="mt-4 space-y-1.5">
        {signals.map((s) => (
          <li key={s.key}>
            <Link
              to="/dashboard/profile-editor"
              className={`flex items-center gap-2 px-2 -mx-2 py-1.5 rounded-md text-sm transition-colors ${
                s.satisfied
                  ? "text-muted-foreground cursor-default pointer-events-none"
                  : "hover:bg-muted text-foreground"
              }`}
              data-testid={`completeness-signal-${s.key}`}
            >
              {s.satisfied ? (
                <CheckCircle weight="fill" size={16} className="text-emerald-500 flex-none" />
              ) : (
                <Circle size={16} weight="duotone" className="text-muted-foreground/60 flex-none" />
              )}
              <span className={s.satisfied ? "line-through" : ""}>{s.label}</span>
              {!s.satisfied && (
                <ArrowRight size={12} className="ml-auto opacity-50" />
              )}
            </Link>
          </li>
        ))}
      </ul>

      {data.truck_slug && (
        <div className="mt-4 pt-4 border-t border-border flex items-center justify-between gap-2">
          <Link
            to={`/t/${data.truck_slug}`}
            className="text-xs font-semibold text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
            data-testid="profile-completeness-view"
          >
            View public profile <ArrowRight size={12} weight="bold" />
          </Link>
          <Link
            to="/dashboard/profile-editor"
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 transition-colors"
            data-testid="profile-completeness-edit"
          >
            Edit profile <ArrowRight size={12} weight="bold" />
          </Link>
        </div>
      )}
    </section>
  );
}
